/**
 * Manage Shop Screen
 * Edit shop profile, theme, and website appearance
 * Prepopulates existing shop details for editing
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, StyleSheet, Alert, TouchableOpacity, Modal, ActivityIndicator, Image, Dimensions } from 'react-native';
import { useAuthTokens, shopManagementService, useCurrentShop, useAuthStore, storageService, type UploadProgress, useThemeColors, useThemeAccent, configurationService, getCustomerShopMenuUrl, shopUrlService, type Country, type ShopType } from '@app/core';
import { Button, Input, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import { ShopHeader } from '@modules/shop-shell';

interface ManageShopScreenProps {
  onSaveSuccess?: () => void;
  onCancel?: () => void;
  onHome?: () => void;
  onManageUser?: () => void;
  onManageCatalog?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

interface ShopForm {
  name: string;
  owner: string;
  address: string;
  email: string;
  mobileCountryCode: string;
  mobileNumber: string;
  shopType: string;
  acceptOnlineOrders: boolean;
  shopImage: string; // Changed to string only, empty string represents no image
  shopUrl: string; // Shop subdomain (e.g., 'shop1' for 'shop1.bazly.in')
  gpsLocation: {
    type: string;
    coordinates: [number, number];
  } | null;
}

interface ThemeData {
  websiteBackground: string;
  bannerColor: string;
  accentColor: string;
}

// Default theme configuration
const DEFAULT_THEME: ThemeData = {
  websiteBackground: '#FFFFFF',
  bannerColor: '#007AFF',
  accentColor: '#FF9500',
};

// Available color options
const THEME_COLORS = [
  { label: 'White', value: '#FFFFFF' },
  { label: 'Light Gray', value: '#F5F5F5' },
  { label: 'Blue', value: '#007AFF' },
  { label: 'Red', value: '#FF3B30' },
  { label: 'Green', value: '#34C759' },
  { label: 'Orange', value: '#FF9500' },
  { label: 'Purple', value: '#AF52DE' },
  { label: 'Dark Gray', value: '#333333' },
  { label: 'Teal', value: '#00B4D8' },
  { label: 'Pink', value: '#FF1493' },
];

export const ManageShopScreen: React.FC<ManageShopScreenProps> = ({
  onSaveSuccess,
  onCancel,
  onHome,
  onManageUser,
  onManageCatalog,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
}) => {
  const [form, setForm] = useState<ShopForm>({
    name: '',
    owner: '',
    address: '',
    email: '',
    mobileCountryCode: '',
    mobileNumber: '',
    shopType: '',
    acceptOnlineOrders: false,
    shopImage: '', // Initialize with empty string instead of null
    shopUrl: '', // Initialize shop subdomain
    gpsLocation: null,
  });

  const [theme, setTheme] = useState<ThemeData>(DEFAULT_THEME);

  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<ShopForm>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [shopImageUrl, setShopImageUrl] = useState<string>(''); // Loaded URL from blobPath
  const [isLive, setIsLive] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [shopTypes, setShopTypes] = useState<ShopType[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Shop URL availability state
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [shopUrlAvailable, setShopUrlAvailable] = useState<boolean | null>(null);
  const [shopUrlMessage, setShopUrlMessage] = useState<string | null>(null);
  const [hasExistingShopUrl, setHasExistingShopUrl] = useState(false);
  const checkAvailabilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shop = useCurrentShop();
  const tokens = useAuthTokens();
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();

  // Load configuration data first, then shop details
  useEffect(() => {
    const initializeScreen = async () => {
      // Load configuration data first
      await loadConfigurationData();
      // Then load shop details
      await loadShopDetails();
    };

    initializeScreen();
  }, []);

  // Load shop image URL from blobPath
  useEffect(() => {
    const loadShopImage = async () => {
      if (form.shopImage && form.shopImage.trim() !== '') {
        try {
          console.log('🖼️ [ManageShop] Loading shop image from blobPath:', form.shopImage);

          // Check if it's already a full URL (for backward compatibility)
          if (form.shopImage.startsWith('http://') || form.shopImage.startsWith('https://')) {
            console.log('ℹ️ [ManageShop] Using full URL (legacy format)');
            setShopImageUrl(form.shopImage);
            return;
          }

          // Load secure read URL from blobPath
          const readUrl = await storageService.getReadUrl(form.shopImage);
          console.log('✅ [ManageShop] Shop image URL loaded');
          setShopImageUrl(readUrl);
        } catch (error) {
          console.error('❌ [ManageShop] Failed to load shop image:', error);
          setShopImageUrl(''); // Clear on error
        }
      } else {
        setShopImageUrl(''); // Clear if no image
      }
    };

    loadShopImage();
  }, [form.shopImage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (checkAvailabilityTimeoutRef.current) {
        clearTimeout(checkAvailabilityTimeoutRef.current);
      }
    };
  }, []);

  const loadConfigurationData = async () => {
    try {
      setLoadingConfig(true);
      console.log('📋 [ManageShop] Loading configuration data...');

      // Load countries and shop types in parallel (uses 5-minute cache)
      const [countriesData, shopTypesData] = await Promise.all([
        configurationService.getCountries({ activeOnly: true }),
        configurationService.getShopTypes({ activeOnly: true }),
      ]);

      console.log('✅ [ManageShop] Configuration data loaded:', {
        countries: countriesData.length,
        shopTypes: shopTypesData.length,
      });

      setCountries(countriesData);
      setShopTypes(shopTypesData);

      // Set default country code if not set and countries are available
      if (!form.mobileCountryCode && countriesData.length > 0) {
        const defaultCountry = countriesData.find(c => c.mobileCountryCode === '91') || countriesData[0];
        console.log('🔧 [ManageShop] Setting default country code:', defaultCountry.mobileCountryCode);
        setForm((prev) => ({
          ...prev,
          mobileCountryCode: defaultCountry.mobileCountryCode,
        }));
      }
    } catch (error) {
      console.error('❌ [ManageShop] Failed to load configuration data:', error);
      Alert.alert(
        'Configuration Error',
        'Failed to load countries and shop types. Some features may not work properly.'
      );
      // Set empty arrays on error
      setCountries([]);
      setShopTypes([]);
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadShopDetails = async () => {
    try {
      setLoading(true);

      if (!tokens?.shopId) {
        throw new Error('Shop ID not found');
      }

      // If shop data is available in store, use it
      if (shop) {
        populateForm(shop);
        return;
      }

      // Otherwise fetch from API
      const shopData = await shopManagementService.getShop(tokens.shopId);
      populateForm(shopData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load shop details';
      Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (shopData: any) => {
    console.log('📝 [ManageShop] Populating form with shop data:', {
      shopType: shopData.shopType,
      mobileCountryCode: shopData.mobileCountryCode,
      mobileNumber: shopData.mobileNumber,
      shopUrl: shopData.shopUrl,
    });

    // Extract subdomain from full URL if present (e.g., 'shop1.bazly.in' -> 'shop1')
    let subdomain = '';
    if (shopData.shopUrl) {
      const match = shopData.shopUrl.match(/^([^.]+)\.bazly\.in$/);
      subdomain = match ? match[1] : shopData.shopUrl;
      setHasExistingShopUrl(true); // Make field non-editable if URL already exists
      setShopUrlAvailable(true); // Existing URL is considered available/valid
    }

    const formData: ShopForm = {
      name: shopData.name || '',
      owner: shopData.owner || '',
      address: shopData.address || '',
      email: shopData.email || '',
      mobileCountryCode: shopData.mobileCountryCode || '',
      mobileNumber: shopData.mobileNumber || '',
      shopType: shopData.shopType || '',
      acceptOnlineOrders: shopData.acceptOnlineOrders || false,
      shopImage: shopData.shopImage || '', // Use empty string instead of null
      shopUrl: subdomain,
      gpsLocation: shopData.gpsLocation || null,
    };

    console.log('✅ [ManageShop] Form data prepared:', formData);

    setForm(formData);
    setIsLive(shopData.isLive || false);

    // Load theme if available
    if (shopData.theme) {
      const themeData: ThemeData = {
        websiteBackground: shopData.theme.colors?.secondary || '#FFFFFF',
        bannerColor: shopData.theme.colors?.primary || '#007AFF',
        accentColor: shopData.theme.lookAndFeel || '#FF9500',
      };
      setTheme(themeData);
    }
  };

  const updateForm = (field: keyof ShopForm, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Handle shop URL real-time availability check
    if (field === 'shopUrl' && !hasExistingShopUrl) {
      handleShopUrlChange(value);
    }
  };

  /**
   * Handle shop URL input change with debounced availability check
   */
  const handleShopUrlChange = (value: string) => {
    // Clear any existing timeout
    if (checkAvailabilityTimeoutRef.current) {
      clearTimeout(checkAvailabilityTimeoutRef.current);
    }

    // Convert to lowercase and remove spaces
    const cleanedValue = value.toLowerCase().replace(/\s+/g, '');

    // Reset availability state
    setShopUrlAvailable(null);
    setShopUrlMessage(null);

    // Validate format first
    const validationError = shopUrlService.getValidationError(cleanedValue);
    if (validationError) {
      setErrors((prev) => ({ ...prev, shopUrl: validationError }));
      return;
    }

    // Clear format validation error
    setErrors((prev) => ({ ...prev, shopUrl: undefined }));

    // Debounce availability check (wait 500ms after user stops typing)
    checkAvailabilityTimeoutRef.current = setTimeout(() => {
      checkShopUrlAvailability(cleanedValue);
    }, 500);
  };

  /**
   * Check shop URL availability via API
   */
  const checkShopUrlAvailability = async (subdomain: string) => {
    if (!subdomain || subdomain.length < 3) {
      return;
    }

    try {
      setCheckingAvailability(true);
      console.log('🔍 [ManageShop] ====== CHECKING SUBDOMAIN AVAILABILITY ======');
      console.log('🔍 [ManageShop] Subdomain:', subdomain);

      const result = await shopUrlService.checkAvailability(subdomain);

      console.log('✅ [ManageShop] ====== AVAILABILITY CHECK RESULT ======');
      console.log('✅ [ManageShop] Available:', result.available);
      console.log('✅ [ManageShop] Message:', result.message);
      console.log('✅ [ManageShop] Full result:', result);

      setShopUrlAvailable(result.available);

      // Store the message from API
      if (result.message) {
        setShopUrlMessage(result.message);
      }

      if (!result.available) {
        // Display the error message from API if available, otherwise use default
        const errorMessage = result.message || 'This subdomain is already taken. Please choose another.';
        console.log('❌ [ManageShop] Subdomain not available:', errorMessage);
        setErrors((prev) => ({
          ...prev,
          shopUrl: errorMessage,
        }));
      } else {
        // Clear any errors if available
        console.log('✅ [ManageShop] Subdomain is available');
        setErrors((prev) => ({
          ...prev,
          shopUrl: undefined,
        }));
      }
    } catch (error: any) {
      console.error('❌ [ManageShop] ====== AVAILABILITY CHECK ERROR ======');
      console.error('❌ [ManageShop] Error:', error);
      console.error('❌ [ManageShop] Error message:', error.message);
      console.error('❌ [ManageShop] Error response:', error.response?.data);
      console.error('❌ [ManageShop] Error status:', error.response?.status);

      setShopUrlAvailable(null);
      setShopUrlMessage(null);

      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to check availability. Please try again.';

      if (error.response?.status === 405) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Availability check service not found. Please contact support.';
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection.';
      }

      setErrors((prev) => ({
        ...prev,
        shopUrl: errorMessage,
      }));
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleImageUpload = async (imageFile: File) => {
    try {
      setUploadingImage(true);
      setUploadProgress(0);

      console.log('📤 [ManageShop] Starting secure image upload:', imageFile.name);

      // Get shop ID from tokens
      const shopId = tokens?.shopId;
      if (!shopId) {
        throw new Error('Shop ID not found. Please log in again.');
      }

      // Validate image file
      const validation = storageService.validateFile(imageFile);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid file');
      }

      // Upload to Azure Blob Storage using secure backend-generated SAS
      const blobPath = await storageService.uploadImage(
        imageFile,
        shopId,
        'shop',
        undefined, // productId not needed for shop images
        (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
          console.log(`📊 [ManageShop] Upload progress: ${progress.percentage}%`);
        }
      );

      console.log('✅ [ManageShop] Image uploaded securely, blobPath:', blobPath);

      // Update form with blobPath (NOT the full URL with SAS token)
      setForm((prev) => {
        const updated = {
          ...prev,
          shopImage: blobPath, // Store only the blob path
        };
        console.log('🔄 [ManageShop] Updated form state with blobPath:', {
          prevShopImage: prev.shopImage,
          newShopImage: updated.shopImage,
        });
        return updated;
      });

      setUploadProgress(100);
      Alert.alert('Success', 'Image uploaded successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      Alert.alert('Upload Error', errorMessage);
      console.error('❌ [ManageShop] Image upload error:', error);
    } finally {
      setUploadingImage(false);
      setTimeout(() => setUploadProgress(0), 1000); // Reset progress after 1 second
    }
  };

  const handleSelectImage = () => {
    // For web, use file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        await handleImageUpload(file);
      }
    };
    input.click();
  };

  const handleRemoveImage = () => {
    Alert.alert('Remove Image', 'Are you sure you want to remove the shop image?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: () => {
          console.log('🗑️ [ManageShop] Removing shop image...');
          console.log('🗑️ [ManageShop] Current shopImage value:', form.shopImage);

          setForm((prev) => {
            const newForm = {
              ...prev,
              shopImage: '', // Set to empty string instead of null for proper API handling
            };
            console.log('✅ [ManageShop] Shop image removed from form state');
            console.log('✅ [ManageShop] New shopImage value:', newForm.shopImage);
            return newForm;
          });

          // Show success feedback
          Alert.alert('Success', 'Shop image has been removed. Remember to save your changes.');
        },
      },
    ]);
  };

  /**
   * Capture GPS location using browser's geolocation API
   */
  const captureGPSLocation = async (): Promise<void> => {
    if (!navigator.geolocation) {
      Alert.alert(
        'Location Not Available',
        'Geolocation is not supported by your browser. You can skip this step and add location later.'
      );
      return;
    }

    setLoadingLocation(true);
    console.log('📍 [ManageShop] Requesting GPS location...');

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout to 15 seconds
          maximumAge: 60000, // Allow cached position up to 1 minute old
        });
      });

      const { latitude, longitude } = position.coords;
      console.log('✅ [ManageShop] GPS location captured:', { latitude, longitude });

      setForm((prev) => ({
        ...prev,
        gpsLocation: {
          type: 'Point',
          coordinates: [longitude, latitude], // GeoJSON format: [longitude, latitude]
        },
      }));

      Alert.alert(
        'Success',
        `Location captured successfully!\nLatitude: ${latitude.toFixed(6)}\nLongitude: ${longitude.toFixed(6)}`
      );
    } catch (err) {
      console.error('❌ [ManageShop] Failed to capture GPS location:', err);
      let errorMessage = 'Unable to capture location.';
      let errorTitle = 'Location Capture Failed';

      if (err instanceof GeolocationPositionError) {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorTitle = 'Permission Denied';
            errorMessage = 'Location access was denied. You can enable it in browser settings or skip this step. Shop location is optional.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorTitle = 'Location Unavailable';
            errorMessage = 'Location information is currently unavailable. You can try again later or skip this step. Shop location is optional.';
            break;
          case err.TIMEOUT:
            errorTitle = 'Request Timeout';
            errorMessage = 'Location request timed out. You can try again or skip this step. Shop location is optional.';
            break;
          default:
            errorMessage = 'An error occurred while capturing location. You can skip this step. Shop location is optional.';
        }
      }

      Alert.alert(errorTitle, errorMessage);
    } finally {
      setLoadingLocation(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ShopForm> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Shop name is required';
    }

    if (!form.owner.trim()) {
      newErrors.owner = 'Owner name is required';
    }

    if (!form.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!form.shopType || !form.shopType.trim()) {
      newErrors.shopType = 'Shop type is required';
    }

    // GPS location is optional - can be added/updated anytime
    // if (!form.gpsLocation) {
    //   newErrors.gpsLocation = 'GPS location is required';
    // }

    // Validate shop URL (mandatory)
    if (!form.shopUrl || !form.shopUrl.trim()) {
      newErrors.shopUrl = 'Shop domain is required';
    } else {
      const validationError = shopUrlService.getValidationError(form.shopUrl);
      if (validationError) {
        newErrors.shopUrl = validationError;
      } else if (!hasExistingShopUrl && shopUrlAvailable === false) {
        newErrors.shopUrl = 'This subdomain is already taken. Please choose another.';
      } else if (!hasExistingShopUrl && shopUrlAvailable === null) {
        newErrors.shopUrl = 'Please wait for availability check to complete';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Check if form has validation errors (for disabling Save button)
   */
  const hasValidationErrors = (): boolean => {
    // Check required fields
    if (!form.name.trim() || !form.owner.trim() || !form.address.trim()) {
      return true;
    }

    if (!form.shopType || !form.shopType.trim()) {
      return true;
    }

    // GPS location is optional - user can save without it
    // if (!form.gpsLocation) {
    //   return true;
    // }

    // Check shop URL
    if (!form.shopUrl || !form.shopUrl.trim()) {
      return true;
    }

    const shopUrlError = shopUrlService.getValidationError(form.shopUrl);
    if (shopUrlError) {
      return true;
    }

    if (!hasExistingShopUrl && (shopUrlAvailable === false || shopUrlAvailable === null)) {
      return true;
    }

    // Check if currently checking availability
    if (checkingAvailability) {
      return true;
    }

    return false;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (!tokens?.shopId) {
        throw new Error('Shop ID not found');
      }

      // Debug: Log form state before save
      console.log('💾 [ManageShop] Form state before save:', {
        shopImage: form.shopImage,
        acceptOnlineOrders: form.acceptOnlineOrders,
        name: form.name,
      });

      // Prepare update request
      const updateData = {
        name: form.name,
        address: form.address,
        owner: form.owner,
        email: form.email,
        mobileCountryCode: form.mobileCountryCode,
        mobileNumber: form.mobileNumber,
        shopType: form.shopType,
        acceptOnlineOrders: form.acceptOnlineOrders,
        shopImage: form.shopImage && form.shopImage.trim() !== '' ? form.shopImage : '', // Send empty string for removed images
        shopUrl: form.shopUrl ? `${form.shopUrl}.bazly.in` : undefined, // Send full URL format
        ...(form.gpsLocation && { gpsLocation: form.gpsLocation }), // Include GPS location if available
        theme: {
          colors: {
            primary: theme.bannerColor,
            secondary: theme.websiteBackground,
          },
          lookAndFeel: theme.accentColor,
        },
      };

      console.log('📡 [ManageShop] Sending update to API:', {
        shopId: tokens.shopId,
        shopImage: updateData.shopImage,
        acceptOnlineOrders: updateData.acceptOnlineOrders,
        hasGpsLocation: !!form.gpsLocation,
      });

      // Update shop via API
      const updatedShop = await shopManagementService.updateShop(tokens.shopId, updateData);

      console.log('✅ [ManageShop] Shop updated successfully:', {
        shopImage: updatedShop.shopImage,
        acceptOnlineOrders: updatedShop.acceptOnlineOrders,
        subscriptionCount: updatedShop.subscriptions?.length || 0,
        hasActiveSubscriptions: (updatedShop.subscriptions || []).some(s => s.isActive),
      });

      // Update shop in store to trigger theme refresh and sync subscriptions
      const authStore = useAuthStore.getState();
      authStore.setShop(updatedShop);

      console.log('✅ [ManageShop] Shop stored in auth store with subscriptions:', {
        shopId: updatedShop.id,
        subscriptions: updatedShop.subscriptions?.length || 0,
      });

      Alert.alert('Success', 'Shop details and theme updated successfully!');

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save shop';
      Alert.alert('Error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <View style={[
        styles.loadingContainer,
        { backgroundColor: themeColors.secondary }
      ]}>
        <ActivityIndicator size="large" color={accentColor} />
        <Text variant="body" color="secondary" style={styles.loadingText}>
          Loading shop details...
        </Text>
      </View>
    );
  }

  // Debug: Log current state values
  console.log('🔍 [ManageShop] Render state:', {
    countriesCount: countries?.length || 0,
    shopTypesCount: shopTypes?.length || 0,
    countriesIsArray: Array.isArray(countries),
    shopTypesIsArray: Array.isArray(shopTypes),
    currentCountryCode: form.mobileCountryCode,
    currentShopType: form.shopType,
    loadingConfig,
  });

  return (
    <>
      {/* Shop Header with Navigation Mode */}
      <ShopHeader
        mode="navigation"
        title="Manage Shop"
        onHome={onHome || onCancel}
        onManageShop={onCancel}
        onManageUser={onManageUser}
        onManageCatalog={onManageCatalog}
        onManageOrders={onManageOrders}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      <ScrollView
        contentContainerStyle={[
          styles.container,
          { backgroundColor: themeColors.secondary }
        ]}
        keyboardShouldPersistTaps="handled"
      >

        {/* Shop Status Indicator - Top Right Corner */}
        <View style={styles.topStatusContainer}>
          <View style={styles.topStatusIndicator}>
            <View
              style={[
                styles.topStatusBall,
                { backgroundColor: isLive ? '#4CAF50' : '#F44336' },
              ]}
            >
              {isLive && <Text style={styles.topStatusTick}>✓</Text>}
            </View>
            <Text variant="body" bold style={styles.topStatusText}>
              {isLive ? 'Live' : 'Offline'}
            </Text>
          </View>
        </View>

        {/* Unified Shop Details Form */}
        <Card style={[styles.formCard, styles.formCardWithStatus]}>
          <Text variant="h3" bold style={styles.sectionTitle}>
            Shop Details
          </Text>

          {/* Accept Online Orders - moved above email */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Accept Online Orders</Text>
            <Text variant="caption" color="secondary" style={styles.fieldDescription}>
              Allow customers to place orders online
            </Text>
            <TouchableOpacity
              style={[
                styles.toggle,
                { backgroundColor: form.acceptOnlineOrders ? '#4CAF50' : '#ccc' },
              ]}
              onPress={() =>
                setForm((prev) => ({
                  ...prev,
                  acceptOnlineOrders: !prev.acceptOnlineOrders,
                }))
              }
            >
              <View
                style={[
                  styles.toggleThumb,
                  {
                    transform: [
                      { translateX: form.acceptOnlineOrders ? 24 : 2 },
                    ],
                  },
                ]}
              />
            </TouchableOpacity>
          </View>

          {/* Email */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Email</Text>
            <Input
              placeholder="shop@example.com"
              value={form.email}
              onChangeText={(value) => updateForm('email', value)}
              keyboardType="email-address"
              testID="manage-shop-email-input"
              editable={false}
              style={styles.readOnlyInput}
            />
          </View>

          {/* Mobile Country Code and Mobile Number */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Mobile Country Code</Text>
            <View style={[styles.pickerContainer, styles.readOnlyPicker]}>
              <Text style={styles.readOnlyPickerText}>
                {form.mobileCountryCode ? (
                  (() => {
                    const country = countries.find(c => c.mobileCountryCode === form.mobileCountryCode);
                    return country ? `${country.countryName} (+${country.mobileCountryCode})` : `+${form.mobileCountryCode}`;
                  })()
                ) : (
                  'Not set'
                )}
              </Text>
            </View>
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Mobile Number</Text>
            <Input
              placeholder="9876543210"
              value={form.mobileNumber}
              onChangeText={(value) => updateForm('mobileNumber', value)}
              keyboardType="phone-pad"
              testID="manage-shop-mobilenumber-input"
              editable={false}
              style={styles.readOnlyInput}
            />
          </View>

          {/* Shop Domain - Mandatory */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Shop Domain *</Text>
            <View style={styles.shopDomainContainer}>
              <Input
                placeholder="myshop"
                value={form.shopUrl}
                onChangeText={(value) => updateForm('shopUrl', value.toLowerCase().replace(/\s+/g, ''))}
                testID="manage-shop-domain-input"
                editable={!hasExistingShopUrl}
                maxLength={63}
                style={[
                  styles.shopDomainInput,
                  hasExistingShopUrl && styles.readOnlyInput
                ]}
                error={errors.shopUrl}
              />
              <Text style={styles.domainSuffix}>.bazly.in</Text>
            </View>

            {/* Availability indicator */}
            {!hasExistingShopUrl && form.shopUrl && form.shopUrl.length >= 3 && (
              <View style={styles.availabilityContainer}>
                {checkingAvailability ? (
                  <View style={styles.availabilityRow}>
                    <ActivityIndicator size="small" color="#007AFF" />
                    <Text variant="caption" color="secondary" style={styles.availabilityText}>
                      Checking availability...
                    </Text>
                  </View>
                ) : shopUrlAvailable === true ? (
                  <View style={styles.availabilityRow}>
                    <Text style={styles.availableIcon}>✓</Text>
                    <Text variant="caption" style={styles.availableText}>
                      Subdomain is valid & Available
                    </Text>
                  </View>
                ) : shopUrlAvailable === false && shopUrlMessage ? (
                  <Text variant="caption" style={styles.unavailableText}>
                    ✗ {shopUrlMessage}
                  </Text>
                ) : null}
              </View>
            )}

            {hasExistingShopUrl && (
              <Text variant="caption" color="secondary" style={styles.helperText}>
                Domain cannot be changed once set
              </Text>
            )}

            {!hasExistingShopUrl && (
              <Text variant="caption" color="secondary" style={styles.helperText}>
                Choose a unique subdomain (3-63 characters, lowercase letters, numbers, and hyphens only)
              </Text>
            )}
          </View>

          {/* Shop Name */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Shop Name</Text>
            <Input
              placeholder="Your shop name"
              value={form.name}
              onChangeText={(value) => updateForm('name', value)}
              error={errors.name}
              testID="manage-shop-name-input"
            />
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Owner Name</Text>
            <Input
              placeholder="Your name"
              value={form.owner}
              onChangeText={(value) => updateForm('owner', value)}
              error={errors.owner}
              testID="manage-shop-owner-input"
            />
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Address</Text>
            <Input
              placeholder="Shop address"
              value={form.address}
              onChangeText={(value) => updateForm('address', value)}
              error={errors.address}
              testID="manage-shop-address-input"
            />
          </View>

          {/* Shop Location Section */}
          <View style={styles.fieldContainer}>
            <View style={styles.gpsLocationHeader}>
              <View style={styles.gpsLocationLabel}>
                <Text style={styles.fieldLabel}>Shop Location (Optional)</Text>
                {form.gpsLocation && (
                  <Text style={styles.locationCapturedText}>
                    Location Captured - Lat: {form.gpsLocation.coordinates[1].toFixed(6)}, Long: {form.gpsLocation.coordinates[0].toFixed(6)}
                  </Text>
                )}
              </View>
              <TouchableOpacity
                style={[
                  styles.gpsCheckbox,
                  {
                    backgroundColor: form.gpsLocation ? '#4CAF50' : '#fff',
                    borderColor: form.gpsLocation ? '#4CAF50' : (errors.gpsLocation ? '#FF3B30' : '#ccc'),
                  },
                ]}
                onPress={captureGPSLocation}
                disabled={loadingLocation}
              >
                {form.gpsLocation && (
                  <Text style={styles.gpsCheckboxCheck}>✓</Text>
                )}
              </TouchableOpacity>
            </View>
            {errors.gpsLocation && (
              <Text style={styles.errorText}>{errors.gpsLocation}</Text>
            )}
            {loadingLocation && (
              <View style={styles.loadingLocationContainer}>
                <ActivityIndicator size="small" color="#007AFF" />
                <Text style={styles.loadingLocationText}>Capturing Location...</Text>
              </View>
            )}
          </View>

          {/* Shop Type */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Shop Type</Text>
            <View style={[styles.pickerContainer, styles.readOnlyPicker]}>
              <Text style={styles.readOnlyPickerText}>
                {form.shopType ? (
                  (() => {
                    const shopType = shopTypes.find(t => t.code === form.shopType);
                    return shopType ? shopType.name : form.shopType;
                  })()
                ) : (
                  'Not set'
                )}
              </Text>
            </View>
            {loadingConfig && (
              <Text variant="caption" color="secondary" style={styles.helperText}>
                Loading shop types...
              </Text>
            )}
            {!loadingConfig && (!shopTypes || shopTypes.length === 0) && (
              <Text variant="caption" color="error" style={styles.helperText}>
                No shop types available
              </Text>
            )}
          </View>


          {/* Shop Image Section */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Shop Image</Text>
            <Text variant="caption" color="secondary" style={styles.fieldDescription}>
              Upload a brand image for your shop (max 5MB)
            </Text>

            {form.shopImage && form.shopImage.trim() !== '' ? (
              <View style={styles.imagePreview}>
                {shopImageUrl ? (
                  <Image
                    source={{ uri: shopImageUrl }}
                    style={styles.previewImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={[styles.previewImage, styles.loadingImagePlaceholder]}>
                    <Text>Loading image...</Text>
                  </View>
                )}
                <View style={styles.imageActions}>
                  <Button
                    onPress={handleSelectImage}
                    variant="secondary"
                    style={styles.imageButton}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? `Uploading ${uploadProgress}%` : 'Change Image'}
                  </Button>
                  <Button
                    onPress={handleRemoveImage}
                    variant="secondary"
                    style={[styles.imageButton, styles.removeButton]}
                    disabled={uploadingImage}
                    testID="remove-image-button"
                  >
                    Remove
                  </Button>
                </View>
                {uploadingImage && uploadProgress > 0 && (
                  <View style={styles.progressContainer}>
                    <View style={[styles.progressBar, { width: `${uploadProgress}%` }]} />
                  </View>
                )}
              </View>
            ) : (
              <TouchableOpacity
                style={styles.uploadPlaceholder}
                onPress={handleSelectImage}
                disabled={uploadingImage}
              >
                <Text variant="h3" style={styles.uploadIcon}>
                  📸
                </Text>
                <Text variant="body" bold style={styles.uploadText}>
                  {uploadingImage ? `Uploading ${uploadProgress}%` : 'Tap to select image'}
                </Text>
                <Text variant="caption" color="secondary" style={styles.uploadSubtext}>
                  {uploadingImage ? 'Please wait...' : 'From gallery, files, or camera'}
                </Text>
                {uploadingImage && uploadProgress > 0 && (
                  <View style={styles.progressContainer}>
                    <View style={[styles.progressBar, { width: `${uploadProgress}%` }]} />
                  </View>
                )}
              </TouchableOpacity>
            )}
          </View>

          {/* Website Theme */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Website Theme</Text>

            {/* Website Background Color */}
            <View style={styles.themeSection}>
              <View style={styles.themeHeader}>
                <Text style={styles.themeSubLabel}>Website Background Color</Text>
                <TouchableOpacity
                  onPress={() => setShowColorPicker('websiteBackground')}
                  style={[
                    styles.colorPreview,
                    { backgroundColor: theme.websiteBackground },
                  ]}
                />
              </View>
              <Text variant="caption" color="secondary" style={styles.themeDescription}>
                Main background color for all website pages
              </Text>
            </View>

            {/* Top Banner Color */}
            <View style={styles.themeSection}>
              <View style={styles.themeHeader}>
                <Text style={styles.themeSubLabel}>Top Banner Color</Text>
                <TouchableOpacity
                  onPress={() => setShowColorPicker('bannerColor')}
                  style={[
                    styles.colorPreview,
                    { backgroundColor: theme.bannerColor },
                  ]}
                />
              </View>
              <Text variant="caption" color="secondary" style={styles.themeDescription}>
                Header and navigation bar color
              </Text>
            </View>

            {/* Accent Color */}
            <View style={styles.themeSection}>
              <View style={styles.themeHeader}>
                <Text style={styles.themeSubLabel}>Accent Color</Text>
                <TouchableOpacity
                  onPress={() => setShowColorPicker('accentColor')}
                  style={[
                    styles.colorPreview,
                    { backgroundColor: theme.accentColor },
                  ]}
                />
              </View>
              <Text variant="caption" color="secondary" style={styles.themeDescription}>
                Color for buttons, links, and highlights
              </Text>
            </View>
          </View>

          {/* Live Preview */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Your Shop Website Live Preview</Text>
            <View
              style={[
                styles.preview,
                {
                  backgroundColor: theme.websiteBackground,
                  borderWidth: 1,
                  borderColor: '#E0E0E0',
                  borderRadius: 8,
                  overflow: 'hidden'
                },
              ]}
            >
              <View
                style={[
                  styles.previewBanner,
                  { backgroundColor: theme.bannerColor },
                ]}
              >
                <Text style={[
                  styles.previewText,
                  {
                    color: theme.bannerColor === '#FFFFFF' || theme.bannerColor === '#F5F5F5' ? '#000000' : '#FFFFFF'
                  }
                ]} bold>
                  {form.name || 'Your Shop Name'}
                </Text>
              </View>
              <View style={[styles.previewContent, { backgroundColor: theme.websiteBackground }]}>
                <TouchableOpacity
                  disabled={true}
                  style={[
                    styles.previewButton,
                    {
                      backgroundColor: theme.accentColor,
                      opacity: 0.6,
                    },
                  ]}
                >
                  <Text style={[
                    styles.previewButtonText,
                    {
                      color: theme.accentColor === '#FFFFFF' || theme.accentColor === '#F5F5F5' ? '#000000' : '#FFFFFF'
                    }
                  ]}>
                    View Menu
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={true}
                  style={[
                    styles.previewButtonSecondary,
                    {
                      borderColor: theme.accentColor,
                      backgroundColor: 'transparent',
                      opacity: 0.6,
                    },
                  ]}
                >
                  <Text style={[
                    styles.previewButtonSecondaryText,
                    { color: theme.accentColor }
                  ]}>
                    Contact Us
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* Action Buttons - moved to bottom of Shop Details card */}
          <View style={styles.cardActionButtons}>
            <Button
              variant="secondary"
              onPress={onCancel}
              style={styles.cancelButton}
              testID="manage-shop-cancel-button"
              disabled={saving}
            >
              Cancel
            </Button>
            <View style={styles.rightButtonsContainer}>
              <Button
                onPress={handleSave}
                disabled={saving || hasValidationErrors()}
                loading={saving}
                style={styles.saveButton}
                testID="manage-shop-save-button"
                themeColor={accentColor}
              >
                {saving ? 'Saving Changes...' : 'Save Changes'}
              </Button>
            </View>
          </View>

          {/* View My Shop - Web Icon Link */}
          {tokens?.shopId && (
            <TouchableOpacity
              onPress={() => {
                const customerViewUrl = getCustomerShopMenuUrl(tokens.shopId);
                window.open(customerViewUrl, '_blank', 'noopener,noreferrer');
              }}
              style={styles.viewShopIconContainer}
              testID="view-my-shop-button"
            >
              <Text style={styles.viewShopIcon}>🌐</Text>
              <Text style={styles.viewShopLabel}>View My Shop</Text>
            </TouchableOpacity>
          )}
        </Card>
      </ScrollView>

      {/* Color Picker Modal */}
      <Modal
        visible={showColorPicker !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setShowColorPicker(null)}
      >
        <View style={styles.colorPickerOverlay}>
          <View style={styles.colorPickerContainer}>
            <Text variant="h3" bold style={styles.colorPickerTitle}>
              Select Color
            </Text>

            <View style={styles.colorGrid}>
              {THEME_COLORS.map((color) => (
                <TouchableOpacity
                  key={color.value}
                  style={styles.colorOption}
                  onPress={() => {
                    if (showColorPicker) {
                      setTheme((prev) => ({
                        ...prev,
                        [showColorPicker]: color.value,
                      }));
                      setShowColorPicker(null);
                    }
                  }}
                >
                  <View
                    style={[
                      styles.colorSwatch,
                      { backgroundColor: color.value },
                    ]}
                  />
                  <Text variant="caption" style={styles.colorLabel}>
                    {color.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Button
              onPress={() => setShowColorPicker(null)}
              style={styles.closeColorPickerButton}
            >
              Close
            </Button>
          </View>
        </View>
      </Modal>
    </>
  );
};

// Get screen dimensions for responsive design
const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth >= 640;
const isDesktop = screenWidth >= 1024;

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: isDesktop ? 32 : isTablet ? 24 : 16,
    paddingVertical: 16,
    ...(isDesktop && {
      maxWidth: 1200,
      alignSelf: 'center',
      width: '100%',
    }),
  },
  preview: {
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 20,
    height: isTablet ? 180 : 150,
  },
  previewBanner: {
    paddingHorizontal: isTablet ? 20 : 16,
    paddingVertical: isTablet ? 16 : 12,
    justifyContent: 'center',
  },
  previewText: {
    color: colors.white,
    fontSize: 16,
  },
  previewContent: {
    flex: 1,
    paddingHorizontal: isTablet ? 20 : 16,
    paddingVertical: 12,
    justifyContent: 'center',
  },
  previewTitle: {
    fontSize: 18,
    marginBottom: 12,
    color: colors.gray9,
  },
  previewButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  previewButtonText: {
    color: colors.white,
    fontSize: 12,
  },
  formCard: {
    marginBottom: isDesktop ? 24 : isTablet ? 20 : 16,
    padding: isDesktop ? 24 : isTablet ? 20 : 16,
  },
  themeCard: {
    marginBottom: isTablet ? 20 : 16,
    padding: isTablet ? 20 : 16,
  },
  actionCard: {
    marginBottom: isTablet ? 20 : 16,
    padding: isTablet ? 20 : 16,
  },
  sectionTitle: {
    marginBottom: isTablet ? 20 : 16,
    fontSize: isTablet ? 18 : 16,
    fontWeight: 'bold',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 8,
    lineHeight: 20,
  },
  mobileInputGroup: {
    flexDirection: isTablet ? 'row' : 'column',
    gap: isTablet ? 12 : 16,
    marginBottom: 16,
    alignItems: isTablet ? 'flex-end' : 'stretch',
  },
  countryCodeInput: {
    flex: isTablet ? 1 : undefined,
    minWidth: 0,
  },
  mobileNumberInput: {
    flex: isTablet ? 2 : undefined,
    minWidth: 0,
  },
  inputGroup: {
    marginBottom: 16,
    width: '100%',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minHeight: 48,
    justifyContent: 'center',
    position: 'relative',
  },
  pickerError: {
    borderColor: colors.error,
    borderWidth: 2,
  },
  picker: {
    width: '100%',
    height: 48,
    paddingHorizontal: 12,
    paddingVertical: 0,
    fontSize: 14,
    color: colors.gray9,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 8,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    // @ts-ignore - Web-specific styles
    ...(typeof window !== 'undefined' && {
      appearance: 'none',
      cursor: 'pointer',
      outlineStyle: 'none',
      backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: '16px',
      paddingRight: 40,
    }),
  } as any,
  themeSection: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  themeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  themeLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
  },
  themeDescription: {
    fontSize: 12,
    color: colors.gray6,
    marginTop: 2,
  },
  resetThemeContainer: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  resetThemeButton: {
    backgroundColor: colors.gray1,
    borderColor: colors.gray4,
  },
  colorPreview: {
    width: 40,
    height: 40,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.gray3,
  },
  helperText: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 12,
    lineHeight: 16,
    paddingLeft: 2,
  },
  errorText: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 12,
    lineHeight: 16,
    paddingLeft: 2,
    fontWeight: '500',
  },
  colorPickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  colorPickerContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    width: '90%',
    maxHeight: '80%',
  },
  colorPickerTitle: {
    marginBottom: 16,
    textAlign: 'center',
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
    justifyContent: 'center',
  },
  colorOption: {
    alignItems: 'center',
    width: '48%',
  },
  colorSwatch: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: colors.gray3,
  },
  colorLabel: {
    textAlign: 'center',
  },
  closeColorPickerButton: {
    width: '100%',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
  },
  infoCard: {
    marginBottom: 24,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  infoValue: {
    textAlign: 'right',
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gray2,
    marginVertical: 16,
  },
  imageSection: {
    marginVertical: 16,
  },
  imageLabel: {
    fontSize: 14,
    marginBottom: 4,
  },
  imageDescription: {
    fontSize: 12,
    marginBottom: 12,
  },
  imagePreview: {
    marginTop: 12,
    width: '100%',
  },
  previewImage: {
    width: '100%',
    height: 'auto',
    minHeight: 200,
    maxHeight: 500,
    aspectRatio: 16 / 9, // Maintain aspect ratio
    borderRadius: 8,
    backgroundColor: colors.gray2,
    marginBottom: 12,
  },
  imageActions: {
    flexDirection: 'row',
    gap: 8,
  },
  imageButton: {
    flex: 1,
  },
  removeButton: {
    backgroundColor: colors.errorLight,
    borderColor: colors.error,
  },
  uploadPlaceholder: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colors.primary,
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
  },
  uploadIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  uploadText: {
    fontSize: 14,
    color: colors.gray9,
    marginBottom: 4,
  },
  uploadSubtext: {
    fontSize: 12,
  },
  toggleSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  toggleLabel: {
    flex: 1,
    marginRight: 16,
  },
  toggle: {
    width: 50,
    height: 28,
    borderRadius: 14,
    padding: 2,
    justifyContent: 'center',
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.white,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.25)',
    elevation: 3, // For Android
  },
  statusSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  // Top status indicator styles
  topStatusContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
  },
  topStatusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    gap: 8,
  },
  topStatusBall: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topStatusTick: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  topStatusText: {
    fontSize: 14,
    color: colors.gray9,
  },
  statusLabel: {
    flex: 1,
    marginRight: 16,
  },
  statusIndicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBall: {
    width: 12,
    height: 12,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tickMark: {
    color: '#fff',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 12,
  },
  readOnlyInput: {
    backgroundColor: colors.gray2,
    // @ts-ignore - Web-specific style
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  readOnlyPicker: {
    backgroundColor: colors.gray2,
    // @ts-ignore - Web-specific style
    cursor: 'not-allowed',
    opacity: 0.7,
    paddingHorizontal: 12,
    paddingVertical: 14,
  },
  readOnlyPickerText: {
    fontSize: 14,
    color: colors.gray7,
    fontWeight: '500',
  },
  fieldContainer: {
    marginBottom: 16,
  },
  infoBox: {
    backgroundColor: '#E8F4FD',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    flexDirection: 'row',
    gap: 8,
    borderWidth: 1,
    borderColor: '#B3D9F2',
  },
  infoIcon: {
    fontSize: 16,
    lineHeight: 20,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
    color: colors.gray8,
  },
  gpsButton: {
    marginBottom: 12,
  },
  gpsLocationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  gpsLocationLabel: {
    flex: 1,
    marginRight: 12,
  },
  locationCapturedText: {
    fontSize: 12,
    color: '#2E7D32',
    marginTop: 4,
    fontWeight: '500',
  },
  gpsCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  gpsCheckboxCheck: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
    lineHeight: 20,
  },
  loadingLocationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  loadingLocationText: {
    fontSize: 14,
    color: '#007AFF',
  },
  customerViewSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  customerViewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 12,
    elevation: 2,
    // @ts-ignore - Web-specific styles
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  customerViewIcon: {
    fontSize: 24,
    lineHeight: 28,
    color: '#fff',
  },
  customerViewContent: {
    flex: 1,
  },
  customerViewLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    lineHeight: 20,
    color: '#fff',
  },
  customerViewSubtext: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    fontStyle: 'italic',
  },
  customerViewArrow: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  previewSubtitle: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  previewButtonSecondary: {
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  previewButtonSecondaryText: {
    fontSize: 12,
    fontWeight: '600',
  },
  previewNote: {
    marginTop: 12,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  // Consistent field styling
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 8,
  },
  fieldDescription: {
    fontSize: 12,
    color: colors.gray6,
    marginBottom: 8,
    lineHeight: 16,
  },
  themeSubLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.gray8,
  },
  formCardWithStatus: {
    marginTop: 60, // Add space below the floating status indicator
  },
  cardActionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  cancelButton: {
    backgroundColor: colors.gray2,
    borderColor: colors.gray4,
    minWidth: 120,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  customerViewButtonSmall: {
    backgroundColor: colors.gray1,
    borderColor: colors.gray3,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minWidth: 100,
  },
  saveButton: {
    minWidth: 140,
  },
  viewShopIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingVertical: 8,
    // @ts-ignore - Web-specific style
    cursor: 'pointer',
  },
  viewShopIcon: {
    fontSize: 32,
    marginBottom: 4,
  },
  viewShopLabel: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: '600',
  },
  // Shop Domain styles
  shopDomainContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  shopDomainInput: {
    flex: 1,
  },
  domainSuffix: {
    fontSize: 16,
    color: colors.gray7,
    fontWeight: '500',
    paddingLeft: 4,
  },
  availabilityContainer: {
    marginTop: 8,
  },
  availabilityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  availabilityText: {
    fontSize: 12,
    color: colors.gray6,
  },
  availableIcon: {
    fontSize: 16,
    color: '#34C759',
    fontWeight: 'bold',
    marginRight: 4,
  },
  availableText: {
    fontSize: 12,
    color: '#34C759',
    fontWeight: '500',
  },
  unavailableText: {
    fontSize: 12,
    color: '#FF3B30',
    fontWeight: '500',
  },
});

